import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Check, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tiers = [
  {
    id: "starter",
    name: "Starter",
    price: 299,
    tag: "Single center · up to 60 children",
    features: [
      "Digital enrollment + DCY 01234 e-sign",
      "Parent app (messaging, photos, daily reports)",
      "Attendance & ratio tracking (OAC 5101:2-12-18)",
      "Up to 5 staff accounts",
      "Email support",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    price: 499,
    featured: true,
    tag: "Single center · up to 100 children",
    features: [
      "Everything in Starter",
      "Staff app with GPS clock-in",
      "Billing & invoicing automation",
      "Lesson plans aligned to Ohio ELDS",
      "All DCY forms library (DCY + CCU)",
      "Unlimited staff accounts",
      "Priority chat support",
    ],
  },
];

export const Pricing = () => {
  const [loadingId, setLoadingId] = useState(null);
  const [config, setConfig] = useState(null);
  const isSandbox = config?.mode === "sandbox";

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/paypal/config`)
      .then((r) => alive && setConfig(r.data))
      .catch(() => alive && setConfig({ configured: false }));
    return () => {
      alive = false;
    };
  }, []);

  const handleSubscribe = async (packageId) => {
    if (!config?.configured) {
      toast.error("PayPal isn't configured yet — please contact us to start a subscription.");
      return;
    }

    // ----- NCP (No-Code Checkout) mode -----------------------------
    // If the owner has only configured PayPal-hosted payment links (instead
    // of the REST Subscriptions API), redirect directly to the PayPal page.
    if (config.flow === "ncp") {
      const url = config.ncp_links?.[packageId];
      if (!url) {
        toast.error("Subscription link missing for this plan — please contact us.");
        return;
      }
      window.location.href = url;
      return;
    }

    // ----- REST Subscriptions API mode -----------------------------
    if (!config?.plans_configured) {
      toast.error("Subscription plans are still being provisioned. Please book a demo.");
      return;
    }
    setLoadingId(packageId);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/paypal/subscriptions`, {
        package_id: packageId,
        origin_url: origin,
      });
      if (res.data?.approve_url) {
        window.location.href = res.data.approve_url;
      } else {
        throw new Error("No approval URL returned by PayPal");
      }
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail || "Could not start checkout. Please try again or book a demo.";
      toast.error(detail);
      setLoadingId(null);
    }
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 sm:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <div className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-emerald-700">
            Pricing
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-medium text-stone-900 tracking-tight">
            Plain pricing. Ohio centers only.
          </h2>
          <p className="mt-4 text-stone-600">
            Flat monthly subscription, no setup fees. Cancel anytime from your PayPal account.
          </p>
          {isSandbox && (
            <div data-testid="paypal-sandbox-badge" className="mt-3 inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold">
              PayPal Sandbox mode — no real charges
            </div>
          )}
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-6 items-stretch max-w-4xl mx-auto">
          {tiers.map((t) => (
            <div
              key={t.id}
              data-testid={`pricing-tier-${t.id}`}
              className={`relative rounded-3xl p-8 flex flex-col ${
                t.featured
                  ? "bg-emerald-950 text-stone-50 border border-emerald-700 shadow-lift lg:scale-[1.03] lg:-translate-y-1"
                  : "bg-white border border-stone-200 shadow-soft"
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-md">
                  Most popular
                </div>
              )}
              <div className="text-xs uppercase tracking-[0.2em] font-semibold opacity-70">{t.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-5xl font-medium">${t.price}</span>
                <span className="text-sm opacity-70">/mo</span>
              </div>
              <div className={`mt-2 text-sm ${t.featured ? "text-emerald-200/80" : "text-stone-500"}`}>
                {t.tag}
              </div>

              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <span
                      className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5 ${
                        t.featured ? "bg-emerald-700 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      <Check className="w-3 h-3" />
                    </span>
                    <span className={t.featured ? "text-emerald-50" : "text-stone-700"}>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`pricing-${t.id}-cta`}
                onClick={() => handleSubscribe(t.id)}
                disabled={loadingId === t.id}
                className={`mt-8 w-full rounded-xl font-semibold py-6 transition-all hover:-translate-y-0.5 ${
                  t.featured
                    ? "bg-orange-500 hover:bg-orange-600 text-white shadow-md hover:shadow-lg"
                    : "bg-emerald-700 hover:bg-emerald-800 text-white shadow-md hover:shadow-lg"
                }`}
              >
                {loadingId === t.id ? (
                  <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Redirecting to PayPal…</>
                ) : (
                  <>Subscribe with PayPal · ${t.price}/mo</>
                )}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-xs text-stone-500 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Secure recurring billing by PayPal · Cancel anytime · Need a custom plan? <a href="#demo" className="text-emerald-700 font-semibold ml-1">Talk to us</a>.
        </div>
      </div>
    </section>
  );
};

export default Pricing;
